/**
 * Progressive-enhancement contact form. Without JS (or if this throws before
 * attaching) the form still works: it's a real <form method="POST"
 * action="https://api.web3forms.com/submit"> that redirects on success. This
 * only intercepts the submit to give an inline success/error state instead
 * of a full-page redirect, and to add a client-side honeypot check.
 */

interface FieldConfig {
  input: HTMLInputElement | HTMLTextAreaElement;
  errorEl: HTMLElement | null;
  validate: (value: string) => string | null;
}

function setFieldError(field: FieldConfig, message: string | null): void {
  const { input, errorEl } = field;
  if (message) {
    input.setAttribute("aria-invalid", "true");
    input.closest(".field")?.setAttribute("data-invalid", "true");
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  } else {
    input.removeAttribute("aria-invalid");
    input.closest(".field")?.removeAttribute("data-invalid");
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.hidden = true;
    }
  }
}

function setStatus(statusEl: HTMLElement, state: "sending" | "success" | "error", message: string): void {
  statusEl.hidden = false;
  statusEl.textContent = message;
  statusEl.dataset.state = state;
}

export function initContactForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-contact-form]");
  if (!form) return;

  const statusEl = form.querySelector<HTMLElement>("[data-form-status]");

  // Landed back here after a no-JS submission redirect (see the hidden
  // "redirect" field) - show the same inline success state retroactively.
  if (statusEl && new URLSearchParams(window.location.search).has("sent")) {
    setStatus(statusEl, "success", "Thanks. Your message is sent. We reply within one business day.");
  }

  const nameInput = form.elements.namedItem("name") as HTMLInputElement | null;
  const emailInput = form.elements.namedItem("email") as HTMLInputElement | null;
  const companyInput = form.elements.namedItem("company") as HTMLInputElement | null;
  const messageInput = form.elements.namedItem("message") as HTMLTextAreaElement | null;
  const honeypot = form.elements.namedItem("_hp") as HTMLInputElement | null;
  const accessKeyInput = form.elements.namedItem("access_key") as HTMLInputElement | null;
  const subjectInput = form.elements.namedItem("subject") as HTMLInputElement | null;
  const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');

  if (!nameInput || !emailInput || !messageInput || !statusEl) return;

  const fields: FieldConfig[] = [
    {
      input: nameInput,
      errorEl: document.getElementById(nameInput.getAttribute("aria-describedby") ?? ""),
      validate: (v) => (v.trim() ? null : "Enter your name."),
    },
    {
      input: emailInput,
      errorEl: document.getElementById(emailInput.getAttribute("aria-describedby") ?? ""),
      validate: (v) => (v.trim() && emailInput.validity.valid ? null : "Enter a valid email address."),
    },
    {
      input: messageInput,
      errorEl: document.getElementById(messageInput.getAttribute("aria-describedby") ?? ""),
      validate: (v) => (v.trim() ? null : "Enter a message."),
    },
  ];

  const validateAll = (): boolean => {
    let valid = true;
    for (const field of fields) {
      const message = field.validate(field.input.value);
      setFieldError(field, message);
      if (message) valid = false;
    }
    return valid;
  };

  for (const field of fields) {
    field.input.addEventListener("blur", () => setFieldError(field, field.validate(field.input.value)));
  }

  const handleSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (!statusEl) return;

    // A real visitor never fills a field that's invisible to them - anything
    // in it means a bot filled every input blindly. Pretend success and stop.
    if (honeypot?.value) {
      setStatus(statusEl, "success", "Thanks. Your message is sent. We reply within one business day.");
      form.reset();
      return;
    }

    if (!accessKeyInput?.value) {
      setStatus(
        statusEl,
        "error",
        "The contact form is not configured yet. Please email contact@northplandigital.com instead.",
      );
      return;
    }

    if (!validateAll()) {
      setStatus(statusEl, "error", "Please fix the highlighted fields and try again.");
      return;
    }

    submitBtn?.setAttribute("disabled", "true");
    setStatus(statusEl, "sending", "Sending…");

    const payload = {
      access_key: accessKeyInput?.value ?? "",
      subject: subjectInput?.value ?? "New enquiry from northplan.digital",
      name: nameInput.value.trim(),
      email: emailInput.value.trim(),
      company: companyInput?.value.trim() ?? "",
      message: messageInput.value.trim(),
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json().catch(() => null)) as { success?: boolean; message?: string } | null;

      if (response.ok && result?.success) {
        setStatus(statusEl, "success", "Thanks. Your message is sent. We reply within one business day.");
        form.reset();
      } else {
        const detail = result?.message ? ` (${result.message})` : "";
        setStatus(
          statusEl,
          "error",
          `Something went wrong sending your message${detail}. Please email contact@northplandigital.com instead.`,
        );
      }
    } catch {
      setStatus(
        statusEl,
        "error",
        "Couldn't reach the server - check your connection and try again, or email contact@northplandigital.com.",
      );
    } finally {
      submitBtn?.removeAttribute("disabled");
    }
  };

  form.addEventListener("submit", (event) => {
    void handleSubmit(event);
  });
}
